import { AppConfig } from '../config/app-config';
import type { PlatformMetadata } from './analytics.types';

/**
 * Display modes that mean "running as the installed app" rather than in a
 * normal browser tab. An installed PWA does not always run as `standalone`:
 * this app's manifest lists `window-controls-overlay` first in
 * `display_override`, so desktop installs launch in that mode. Checking only
 * `standalone` would misclassify those sessions as browser.
 */
const INSTALLED_APP_DISPLAY_MODES = [
  'standalone',
  'window-controls-overlay',
  'fullscreen',
  'minimal-ui',
] as const;

/**
 * Broad "running as the installed app" check (includes `fullscreen`/`minimal-ui`)
 * — used for ANALYTICS platform classification, where an installed app that has
 * entered fullscreen (e.g. during a VDI session) should still count as the app.
 *
 * Do NOT use this for the browser-access gate: a normal browser tab can be in
 * fullscreen (macOS green button / F11), which would be misread as installed.
 * Use `isInstalledAppMode()` for the gate instead.
 */
export function isStandaloneDisplayMode(): boolean {
  if (typeof window === 'undefined') return false;
  const matchesInstalledMode = INSTALLED_APP_DISPLAY_MODES.some(
    (mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches === true,
  );
  if (matchesInstalledMode) return true;
  // iOS Safari installed PWA
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

/**
 * Display modes an INSTALLED instance of THIS app actually launches in. The
 * manifest declares `display: standalone` with `display_override:
 * [window-controls-overlay, standalone]`, so an install runs as one of these
 * (or, on iOS, `navigator.standalone`). Deliberately excludes `fullscreen` and
 * `minimal-ui`: a plain browser tab can be fullscreen (macOS green button / F11),
 * which must never be mistaken for the installed app — otherwise the
 * browser-access gate is silently bypassed. The installed app entering
 * fullscreen (e.g. a VDI session) is handled separately by the per-context
 * marker in `isInstalledAppMode()`, not by widening this set.
 */
const INSTALLED_APP_LAUNCH_MODES = ['standalone', 'window-controls-overlay'] as const;

/**
 * Per-browsing-context marker recording that THIS window was confirmed to be
 * running as the installed app at some point in its lifetime. Stored in
 * `sessionStorage`, which is scoped to the top-level browsing context and
 * survives a reload, but is NOT shared with other tabs/windows — so a plain
 * browser tab can never inherit it (unlike the origin-wide `localStorage`
 * PWA_INSTALLED flag). This is what lets a fullscreen reload stay recognised as
 * the installed app while a browser tab in F11/green-button fullscreen does not.
 */
const INSTALLED_CONTEXT_KEY = 'jiopc-installed-context';

/** True only when the window is CURRENTLY in a strict installed launch mode. */
function inInstalledLaunchMode(): boolean {
  const matchesLaunchMode = INSTALLED_APP_LAUNCH_MODES.some(
    (mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches === true,
  );
  if (matchesLaunchMode) return true;
  // iOS Safari installed PWA
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

function rememberInstalledContext(): void {
  try {
    sessionStorage.setItem(INSTALLED_CONTEXT_KEY, '1');
  } catch {
    // sessionStorage unavailable (private mode / disabled) — non-fatal. The
    // strict display-mode check still governs; we only lose the fullscreen-reload
    // resilience for this session.
  }
}

export function clearInstalledContext(): void {
  try {
    sessionStorage.removeItem(INSTALLED_CONTEXT_KEY);
  } catch {
    // Storage unavailable — there is no marker to clear in this context.
  }
}

function hasInstalledContext(): boolean {
  try {
    return sessionStorage.getItem(INSTALLED_CONTEXT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Strict "launched as the installed app" check for the browser-access gate.
 *
 * Returns true when the window is currently in a strict installed launch mode
 * (`standalone` / `window-controls-overlay` / iOS `navigator.standalone`), OR
 * when THIS browsing context was previously confirmed installed (the
 * `sessionStorage` marker set on the first such detection).
 *
 * The marker is what makes fullscreen safe (Bug 38959). The installed app
 * legitimately runs `fullscreen` — a VDI session, or the user full-screening the
 * window — and on a reload the only display mode reported is `fullscreen`, which
 * the strict launch set deliberately excludes so a plain browser tab in
 * F11/green-button fullscreen cannot bypass the gate. A browser tab is never a
 * strict launch mode. If "Open in Chrome" reparents an installed PWA context,
 * the explicit `browser` display mode clears its inherited marker. Do not infer
 * a browser context from `fullscreen`: macOS uses that same mode when a user
 * fullscreenes the installed PWA window.
 */
export function isInstalledAppMode(): boolean {
  if (typeof window === 'undefined') return false;
  if (inInstalledLaunchMode()) {
    rememberInstalledContext();
    return true;
  }
  if (window.matchMedia?.('(display-mode: browser)').matches === true) {
    clearInstalledContext();
    return false;
  }
  return hasInstalledContext();
}

/** Poll cadence and cap for the level-triggered safety net in `whenInstalledDisplayMode`. */
const INSTALLED_MODE_POLL_MS = 150;
const INSTALLED_MODE_POLL_TIMEOUT_MS = 8000;

/**
 * Invokes `callback` as soon as the document is — or becomes — an installed-app
 * display mode. Fires synchronously if already installed; otherwise watches the
 * installed-mode media queries (`standalone`, `window-controls-overlay`) for the
 * settle INTO one of them, plus a bounded poll as a level-triggered safety net
 * (see Bug 39196 note in the body).
 *
 * It watches the destination queries rather than `(display-mode: browser)` on
 * purpose: right after install the auto-opened window can settle from
 * `fullscreen` (e.g. installing from a fullscreen browser) straight to
 * `standalone`, never passing through `browser`. Listening to the browser query
 * would miss that transition and strand the user on the install/launch screen.
 * Uses the strict installed-app check so a gated browser tab entering fullscreen
 * still can't be mistaken for an install. Returns a cleanup function.
 */
export function whenInstalledDisplayMode(callback: () => void): () => void {
  if (isInstalledAppMode()) {
    callback();
    return () => {};
  }
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }

  let settled = false;
  let pollId: ReturnType<typeof setInterval> | undefined;
  let stopPollId: ReturnType<typeof setTimeout> | undefined;

  const queries = INSTALLED_APP_LAUNCH_MODES.map((mode) =>
    window.matchMedia(`(display-mode: ${mode})`),
  );

  const stopPolling = () => {
    if (pollId !== undefined) {
      clearInterval(pollId);
      pollId = undefined;
    }
    if (stopPollId !== undefined) {
      clearTimeout(stopPollId);
      stopPollId = undefined;
    }
  };
  const remove = () => {
    stopPolling();
    queries.forEach((q) => q.removeEventListener?.('change', handler));
  };
  function fire(): void {
    if (settled || !isInstalledAppMode()) return;
    settled = true;
    remove();
    callback();
  }
  function handler(): void {
    fire();
  }

  queries.forEach((q) => q.addEventListener?.('change', handler));

  // Level-triggered safety net (Bug 39196). A media query can settle INTO an
  // installed mode WITHOUT dispatching a `change` event to a listener attached a
  // moment earlier — notably right after a service-worker update reload of an
  // installed PWA on macOS, where the strict display-mode reads non-installed for
  // a beat and the per-context sessionStorage marker does not survive the reload.
  // With change-events alone the missed transition strands the user on the
  // install/launch ("Launch Now") screen until they fully close and reopen the
  // app. Polling re-checks the real state so recovery no longer depends on
  // catching one ephemeral event. Bounded: a genuine browser tab never becomes
  // installed, so it must not keep a timer alive forever (nor ever be un-blocked).
  pollId = setInterval(fire, INSTALLED_MODE_POLL_MS);
  stopPollId = setTimeout(stopPolling, INSTALLED_MODE_POLL_TIMEOUT_MS);

  return remove;
}

/**
 * Invokes `callback` when the document is — or becomes — a plain browser tab
 * (`display-mode: browser`). Used to re-apply the browser-access gate to a tab
 * that slipped past the one-shot bootstrap check by momentarily mis-reporting an
 * installed display mode at load (e.g. a tab spawned from the PWA via
 * "Open in Chrome") and then settling to `browser`.
 *
 * Safe by construction: an installed PWA never enters `browser` display mode — a
 * VDI session is `fullscreen` and normal use is `standalone`/`window-controls-overlay`
 * — so this never fires for the real app. Returns a cleanup function.
 */
export function whenBrowserDisplayMode(callback: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }
  const query = window.matchMedia('(display-mode: browser)');
  if (query.matches) callback();
  const handler = () => {
    if (query.matches) callback();
  };
  query.addEventListener?.('change', handler);
  return () => query.removeEventListener?.('change', handler);
}

export function parseBrowserName(ua: string): string {
  const lower = ua.toLowerCase();
  if (lower.includes('edg/')) return 'edge';
  if (lower.includes('chrome/') && !lower.includes('edg/')) return 'chrome';
  if (lower.includes('firefox/')) return 'firefox';
  if (lower.includes('safari/') && !lower.includes('chrome/')) return 'safari';
  return 'unknown';
}

function parseBrowserVersion(ua: string, platformName: string): string {
  const patterns: Record<string, RegExp> = {
    chrome: /Chrome\/([\d.]+)/i,
    firefox: /Firefox\/([\d.]+)/i,
    safari: /Version\/([\d.]+)/i,
    edge: /Edg\/([\d.]+)/i,
  };
  const re = patterns[platformName];
  if (!re) return '';
  const match = ua.match(re);
  return match?.[1] ?? '';
}

function parseDeviceOs(ua: string): string {
  const win = ua.match(/Windows NT ([\d.]+)/i);
  if (win) return `windows ${win[1]}`;

  const mac = ua.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/i);
  if (mac) return `macos ${mac[1].replace(/_/g, '.')}`;

  const android = ua.match(/Android ([\d.]+)/i);
  if (android) return `android ${android[1]}`;

  const ios = ua.match(/OS (\d+[._]\d+(?:[._]\d+)?) like Mac OS X/i);
  if (ios) return `ios ${ios[1].replace(/_/g, '.')}`;

  if (/Linux/i.test(ua)) return 'linux';
  return 'unknown';
}

function parseDeviceType(ua: string): string {
  if (/Tablet|iPad/i.test(ua)) return 'tablet';
  if (/Mobi|Android.*Mobile|iPhone/i.test(ua)) return 'mobile';
  // Desktops/laptops are not reliably distinguishable from UA alone.
  if (/Macintosh|Mac OS X/i.test(ua)) return 'laptop';
  if (/Windows|Linux/i.test(ua)) return 'desktop';
  return 'desktop';
}

/** True only for phone user-agents. Tablets (iPad / Android tablet) and desktop/laptop browsers return false. */
export function isMobilePhone(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): boolean {
  return parseDeviceType(ua) === 'mobile';
}

function parseDeviceName(ua: string, deviceType: string): string {
  if (/Macintosh|Mac OS X/i.test(ua)) return 'mac';
  if (/Windows/i.test(ua)) return deviceType === 'laptop' ? 'windows_pc' : 'windows_pc';
  if (/iPhone/i.test(ua)) return 'iphone';
  if (/iPad/i.test(ua)) return 'ipad';
  if (/Android/i.test(ua)) return 'android_device';
  if (/Linux/i.test(ua)) return 'linux_pc';
  return 'unknown';
}

/**
 * Best-effort device/platform snapshot sent as the request's nested
 * `platformMetadata` object. Pure helper (no DI) so it is easy to unit-test.
 */
export function buildPlatformMetadata(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  standalone: boolean = isStandaloneDisplayMode(),
): PlatformMetadata {
  const device_type = parseDeviceType(ua);
  const shared = {
    device_type,
    device_name: parseDeviceName(ua, device_type),
    device_os: parseDeviceOs(ua),
  };

  // platform_version is the app version when installed, the browser version otherwise.
  if (standalone) {
    return {
      ...shared,
      platform: 'application',
      platform_name: 'pwa_application',
      platform_version: AppConfig.VERSION_NAME,
    };
  }

  const platform_name = parseBrowserName(ua);
  return {
    ...shared,
    platform: 'browser',
    platform_name,
    platform_version: parseBrowserVersion(ua, platform_name) || AppConfig.VERSION_NAME,
  };
}
